import { minutesToMilliseconds } from 'date-fns';
import { useEffect, useRef } from 'react';

import { useTranslate } from '@growtherapy/i18n';
import { Body, Button, Link, Modal, ModalBody, Subhead } from '@growtherapy/sprout-ui';

import { Translate } from '../../i18n';
import { AI_FEATURES_FAQ_LINK } from './constants';

export type ConsentModalProps = {
  onDeclineConsent: () => void;
  onConsent: () => Promise<void>;
  onDismissConsent: () => void;
  isLoading: boolean;
  isOpen: boolean;
  autoDismissTimeout?: number;
  providerName: string;
  isMultiPatientSession: boolean;
  showRefreshedCopy?: boolean;
};

export function ConsentModal({
  onDeclineConsent,
  onConsent,
  onDismissConsent,
  isLoading,
  isOpen,
  providerName,
  isMultiPatientSession,
  showRefreshedCopy = false,
  autoDismissTimeout = minutesToMilliseconds(2),
}: ConsentModalProps) {
  const t = useTranslate();
  const consentButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(
    function dismissAfterTwoMinutes() {
      if (!isOpen) return;
      const timeout = setTimeout(function dismissModal() {
        onDismissConsent();
      }, autoDismissTimeout);
      return function cleanup() {
        clearTimeout(timeout);
      };
    },
    [isOpen, autoDismissTimeout, onDismissConsent],
  );

  useEffect(
    function focusConsentButtonOnOpen() {
      if (!isOpen || !showRefreshedCopy) return;
      const frame = requestAnimationFrame(() => consentButtonRef.current?.focus());
      return () => cancelAnimationFrame(frame);
    },
    [isOpen, showRefreshedCopy],
  );

  return (
    <Modal
      onClose={onDismissConsent}
      isOpen={isOpen}
      className='shadow-none sm:max-w-[31rem] md:max-w-[31rem] sm:top-[25%] rebrand:bg-surface-inverse'
      onOutsideClick={onDismissConsent}
    >
      <ModalBody className='flex flex-col items-center gap-4 p-8 text-center'>
        <div className='flex flex-col gap-2 items-center'>
          <Subhead.Default as='h2' className='rebrand:text-text-inverse'>
            {showRefreshedCopy
              ? t('telehealth-core:consent.transcriptionConsent')
              : `${providerName} ${t('telehealth-core:consent.wouldLikeToUseGrowTherapysCapability')}`}
          </Subhead.Default>
        </div>
        <div
          className={
            showRefreshedCopy ? 'w-full flex flex-col gap-3 text-left' : 'w-full flex flex-col items-center text-center'
          }
        >
          {isMultiPatientSession && (
            <Body.Small className='rebrand:text-text-inverse'>
              {t('telehealth-core:consent.consentWillApplyToAllParticipants')}
            </Body.Small>
          )}
          {showRefreshedCopy ? (
            <Body.Small className='rebrand:text-text-inverse'>
              <Translate
                i18nKey='telehealth-core:consent.growUsesAiTranscriptionToAidWithLink'
                components={{
                  1: <Link className='font-normal rebrand:text-text-inverse' external href={AI_FEATURES_FAQ_LINK} />,
                }}
              />
            </Body.Small>
          ) : (
            <Body.Small className='rebrand:text-text-inverse'>
              {t('telehealth-core:consent.youCanChangeThisSettingAtAnyTime')}
            </Body.Small>
          )}
        </div>
        <div className='flex flex-col sm:flex-row gap-4 w-full'>
          <Button
            disabled={isLoading}
            onClick={onDeclineConsent}
            use='primary'
            className='w-full rebrand:border-border-default'
          >
            {showRefreshedCopy
              ? t('telehealth-core:consent.noIDoNotConsentToTranscription')
              : t('telehealth-core:consent.noIDontConsent')}
          </Button>
          <Button ref={consentButtonRef} loading={isLoading} onClick={onConsent} use='secondary' className='w-full m-0'>
            {showRefreshedCopy
              ? t('telehealth-core:consent.yesIConsentToTranscription')
              : t('telehealth-core:consent.yesIConsent')}
          </Button>
        </div>
        {showRefreshedCopy && (
          <Body.Xsmall className='rebrand:text-text-inverse w-full text-left'>
            <Translate
              i18nKey='telehealth-core:consent.pleaseBeAwareThatThisTechnologyCanMakeMistakesWithLink'
              components={{
                1: <Link className='font-normal rebrand:text-text-inverse' external href={AI_FEATURES_FAQ_LINK} />,
              }}
            />
          </Body.Xsmall>
        )}
      </ModalBody>
    </Modal>
  );
}
